import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import SectionHeading from "@/components/marketing/SectionHeading";
import OutletCard from "@/components/marketing/service/OutletCard";
import Reveal from "@/components/marketing/Reveal";

/**
 * Proof block for service pages. Renders a grid of named outlets with sector,
 * region, DA, tier, and reach detail. Each card links to the public outlet
 * list. The heading, subtitle, formats note, and optional CTA are passed per
 * page so the component stays reusable across services.
 *
 * Optional props:
 *   filter   - React node rendered between the heading and the grid (e.g. sector tabs)
 *   gridKey  - when this changes, the grid cross-fades/scales out then in (animated swap)
 *   notice   - when set, a centered message replaces the grid (used for sparse / loading states)
 */
export default function OutletGrid({ outlets, title, subtitle, formatsNote, cta, filter, gridKey, notice, loading, error, onRetry }) {
  return (
    <section className="py-7 sm:py-9 lg:py-12">
      <Container>
        <Reveal>
          <SectionHeading tag="Available Outlets" title={title} subtitle={subtitle} />
        </Reveal>

        {filter}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[rgba(255,255,255,0.58)] backdrop-blur-xl border border-[rgba(255,255,255,0.85)] rounded-2xl px-6 py-5 animate-pulse">
                <div className="h-14 mb-5 bg-[rgba(139,92,246,0.08)] rounded" />
                <div className="flex items-end justify-between gap-3">
                  <div className="space-y-2">
                    <div className="h-3 w-20 bg-[rgba(139,92,246,0.08)] rounded" />
                    <div className="h-4 w-16 bg-[rgba(139,92,246,0.08)] rounded" />
                  </div>
                  <div className="h-4 w-12 bg-[rgba(139,92,246,0.08)] rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-steel text-base mb-4">Couldn't load outlets. Please try again.</p>
            <Button onClick={onRetry} variant="outline">Retry</Button>
          </div>
        ) : notice ? (
          <div className="text-center py-16">
            <p className="text-steel text-base max-w-md mx-auto">{notice}</p>
          </div>
        ) : (
          <Reveal variant="card" delay={0.08}>
            <AnimatePresence mode="wait">
            <motion.div
              key={gridKey || "default"}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
            >
              {outlets.map((outlet) => (
                <OutletCard key={outlet.name} outlet={outlet} />
              ))}
            </motion.div>
            </AnimatePresence>
          </Reveal>
        )}

        {formatsNote && (
          <p className="text-center text-steel text-sm mt-10 max-w-2xl mx-auto">
            {formatsNote}
          </p>
        )}

        {cta && (
          <div className="flex justify-center mt-10">
            <Button asChild size="lg">
              <Link to={cta.to}>
                {cta.label}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        )}
      </Container>
    </section>
  );
}